import { useEffect, useId, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { RoutineTierHint } from '@/components/routines/tier';
import { SystemAccessBadge, SystemAccessSummary } from './SystemAccessStatus';
import {
  clearAll,
  groupState,
  MAX_SYSTEMS_PER_RUN,
  selectAll,
  selectedIds,
  selectionCounts,
  selectionProblems,
  toggleGroup,
  toggleSystem,
  type BatchSelection,
} from './batchSelection';
import type {
  MaintenancePreviewExclusion,
  MaintenancePreviewGroup,
} from '@/hooks/useRuns';

/**
 * Schritt 1 der Komplettwartung: was würde laufen?
 *
 * Eine Gruppe = eine Routine = später ein Lauf. Gruppen sind komplett
 * an-/abwaehlbar, die Systemliste je Gruppe ist eingeklappt (Kunden mit 200
 * Systemen sollen nicht 200 Zeilen am Stück bekommen) und darin lässt sich
 * jedes System einzeln abwählen.
 *
 * Ausgeschlossene Systeme werden NICHT versteckt: sie stehen zusammengeklappt
 * unter der Liste, mit Grund. Wer einen Host vermisst, muss ihn dort finden.
 *
 * WICHTIG: Die Gruppen kommen vom Server bereits in Ausführungsreihenfolge
 * (execution_order der Routine, kleiner = früher). Diese Liste wird deshalb
 * NIE umsortiert - die Nummerierung wäre sonst gelogen, und ein "Update
 * (apply)" vor dem "Update-Check" fällt niemandem mehr auf.
 */

interface BatchScopeStepProps {
  customerId: string;
  customerName: string;
  groups: MaintenancePreviewGroup[];
  excluded: MaintenancePreviewExclusion[];
  selection: BatchSelection;
  onSelectionChange: (next: BatchSelection) => void;
  loading: boolean;
  error: Error | null;
  onNext: () => void;
}

export function BatchScopeStep({
  customerId,
  customerName,
  groups,
  excluded,
  selection,
  onSelectionChange,
  loading,
  error,
  onNext,
}: BatchScopeStepProps): JSX.Element {
  const counts = selectionCounts(groups, selection);
  const allSelected =
    groups.length > 0 && groups.every((g) => groupState(selection, g) === 'all');
  // Die Vorschau kennt die Endpunkt-Limits nicht und hängt erst mal alles an.
  // Hier abfangen, sonst antwortet der Start mit einer rohen 422/400-Meldung.
  const problems = selectionProblems(groups, selection);

  const nextButton = (position: 'oben' | 'unten'): JSX.Element => (
    <Button
      rightIcon={<ArrowRight className="size-4" />}
      disabled={counts.runs === 0 || problems.length > 0}
      onClick={onNext}
      aria-label={`Weiter, ${counts.runs} ${counts.runs === 1 ? 'Lauf' : 'Läufe'} vorbereitet (${position})`}
      data-testid={`batch-next-${position === 'oben' ? 'top' : 'bottom'}`}
    >
      {`Weiter (${counts.runs})`}
    </Button>
  );

  return (
    <Card
      title="Umfang prüfen"
      description={`Alles, was bei ${customerName} gewartet werden kann - gruppiert nach Routine, in Ausführungsreihenfolge.`}
      actions={groups.length > 0 ? nextButton('oben') : undefined}
    >
      {loading && <SkeletonRows rows={4} cols={3} />}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && groups.length === 0 && (
        <EmptyState
          title="Nichts zu warten"
          description={`Für ${customerName} passt aktuell keine Routine auf ein System. Ausgeschlossene Systeme stehen unten mit Grund.`}
          actions={
            <Link to="/runs/new" search={{ customers: customerId }}>
              <Button variant="secondary">Einzelne Routine wählen</Button>
            </Link>
          }
        />
      )}

      {problems.length > 0 && (
        <div
          className="mb-3 rounded-md border border-warning/40 bg-warning/5 p-3"
          role="alert"
          data-testid="batch-scope-problems"
        >
          <p className="flex items-center gap-2 text-sm font-medium">
            <ShieldAlert className="size-4" aria-hidden="true" />
            So kann der Vorgang nicht starten
          </p>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                onSelectionChange(allSelected ? clearAll(groups) : selectAll(groups))
              }
              data-testid="batch-toggle-all"
            >
              {allSelected ? 'Alles abwählen' : 'Alles anwählen'}
            </Button>
            <p className="text-sm" aria-live="polite" data-testid="batch-scope-count">
              <strong>{counts.runs}</strong> von {groups.length}{' '}
              {groups.length === 1 ? 'Routine' : 'Routinen'} ausgewählt,{' '}
              <strong>{counts.systems}</strong>{' '}
              {counts.systems === 1 ? 'System' : 'Systeme'}
            </p>
          </div>

          <p className="mb-2 text-xs text-muted" data-testid="batch-order-hint">
            Abgearbeitet wird von oben nach unten, in dieser Reihenfolge (1 zuerst).
            Sie kommt aus der Routine selbst (<code className="font-mono">execution_order</code>{' '}
            im Playbook-Header) &mdash; Abwaehlen laesst Schritte aus, verschiebt sie aber
            nicht.
          </p>

          {/* Server-Reihenfolge = Ausführungsreihenfolge. Hier NICHT sortieren. */}
          <ul className="flex flex-col gap-2" data-testid="batch-groups">
            {groups.map((group, index) => (
              <BatchGroupItem
                key={group.routine.code}
                group={group}
                position={index + 1}
                total={groups.length}
                selection={selection}
                onSelectionChange={onSelectionChange}
              />
            ))}
          </ul>
        </>
      )}

      {!loading && !error && (
        <ExcludedList excluded={excluded} />
      )}

      <footer className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/runs/new"
          search={{ customers: customerId }}
          className="text-sm text-muted hover:text-text hover:underline"
          data-testid="batch-single-routine-link"
        >
          Stattdessen eine einzelne Routine wählen
        </Link>
        {groups.length > 0 && nextButton('unten')}
      </footer>
    </Card>
  );
}

interface BatchGroupItemProps {
  group: MaintenancePreviewGroup;
  /** 1-basierte Position in der Ausführungsfolge (Server-Reihenfolge). */
  position: number;
  total: number;
  selection: BatchSelection;
  onSelectionChange: (next: BatchSelection) => void;
}

function BatchGroupItem({
  group,
  position,
  total,
  selection,
  onSelectionChange,
}: BatchGroupItemProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const checkboxRef = useRef<HTMLInputElement>(null);
  const state = groupState(selection, group);
  const chosen = selectedIds(selection, group.routine.code);
  const { routine, systems } = group;

  // "Teilweise gewaehlt" gibt es nur als DOM-Property, nicht als Attribut.
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = state === 'some';
  }, [state]);

  return (
    <li className="rounded-md border border-border" data-testid={`batch-group-${routine.code}`}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <span
          className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-xs font-semibold tabular-nums"
          title={`Schritt ${position} von ${total} in der Abarbeitungsfolge`}
          data-testid={`batch-group-position-${routine.code}`}
        >
          {position}
        </span>
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={state !== 'none'}
          onChange={() => onSelectionChange(toggleGroup(selection, group))}
          aria-label={`Routine ${routine.display_name} komplett an- oder abwählen`}
          className="size-4 accent-primary"
          data-testid={`batch-group-toggle-${routine.code}`}
        />
        <div className="min-w-[14rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{routine.display_name}</span>
            <Badge tone="neutral">{routine.code}</Badge>
            {routine.is_destructive && (
              <Badge tone="danger">
                <span className="inline-flex items-center gap-1">
                  <ShieldAlert className="size-3" aria-hidden="true" /> destruktiv
                </span>
              </Badge>
            )}
            {routine.requires_snapshot && !routine.is_destructive && (
              <Badge tone="primary">Snapshot</Badge>
            )}
            {/* Reifegrad bewusst als dezenter Text und nicht als Badge: die
                Zeile trägt schon Name, Code und ggf. "destruktiv"/"Snapshot"/
                "bewusst waehlen". Bis 13.08.2026 hing hier außerdem bei JEDER
                Routine ein gelbes "experimental", weil der Wert hartkodiert
                war - zehn gelbe Marken untereinander warnen vor gar nichts.
                Produktive Routinen zeigen hier jetzt nichts mehr. */}
            <RoutineTierHint
              tier={routine.tier}
              data-testid={`batch-group-tier-${routine.code}`}
            />
            {routine.auto_select === false && (
              <Badge tone="warning">bewusst wählen</Badge>
            )}
          </div>
          {routine.auto_select === false && (
            <p className="mt-1 text-xs text-warning">
              Nicht vorausgewählt: greift tiefer ein als die Routinewartung. Nur
              anhaken, wenn du das ausdrücklich willst.
            </p>
          )}
          <p className="mt-1 text-xs text-muted" data-testid={`batch-group-count-${routine.code}`}>
            {chosen.length} von {systems.length}{' '}
            {systems.length === 1 ? 'System' : 'Systemen'} &middot;{' '}
            {chosen.length > 0 ? 'ergibt einen Lauf' : 'kein Lauf'}
            {chosen.length > MAX_SYSTEMS_PER_RUN && (
              <span className="text-danger">
                {' '}
                &middot; {chosen.length - MAX_SYSTEMS_PER_RUN} zu viel für einen Lauf
              </span>
            )}
          </p>
          {/* Zugangslage der Gruppe, sichtbar OHNE Aufklappen - die Liste
              startet eingeklappt, und vor dem Start klappt sie niemand auf. */}
          <SystemAccessSummary
            systems={systems}
            className="mt-1"
            data-testid={`batch-group-access-${routine.code}`}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          rightIcon={
            open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />
          }
          data-testid={`batch-group-expand-${routine.code}`}
        >
          {open ? 'Systeme ausblenden' : `Systeme zeigen (${systems.length})`}
        </Button>
      </div>

      {open && (
        <ul
          id={panelId}
          className="flex flex-col gap-1 border-t border-border bg-surface-2/40 p-3"
        >
          {systems.map((sys) => (
            <li key={sys.id}>
              <label className="flex cursor-pointer flex-wrap items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-surface-2">
                <input
                  type="checkbox"
                  checked={chosen.includes(sys.id)}
                  onChange={() => onSelectionChange(toggleSystem(selection, group, sys.id))}
                  className="size-4 accent-primary"
                  data-testid={`batch-system-${routine.code}-${sys.id}`}
                />
                <span className="font-mono text-xs">{sys.display_name ?? sys.hostname}</span>
                {sys.display_name && sys.display_name !== sys.hostname && (
                  <span className="text-xs text-muted">({sys.hostname})</span>
                )}
                {/* Hinweis, keine Sperre: die Checkbox bleibt aktiv, auch wenn
                    der Zugang zuletzt fehlte - die Meldung kann veraltet sein. */}
                <SystemAccessBadge
                  system={sys}
                  className="ml-auto"
                  data-testid={`batch-system-access-${routine.code}-${sys.id}`}
                />
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Der Preview-Endpunkt liefert den Grund heute als deutschen Klartext; diese
 * Map übersetzt nur die Code-Schreibweise, die dieselben Gründe an anderen
 * Stellen (Wizard-Badges, ältere Responses) tragen. Alles Unbekannte wird
 * roh angezeigt statt geschluckt - lieber technisch als unsichtbar.
 */
const EXCLUSION_LABELS: Record<string, string> = {
  out_of_scope: 'Nicht im Wartungs-Scope (excluded/eol/offline/external)',
  excluded: 'Scope: excluded',
  eol: 'Scope: eol',
  offline: 'Scope: offline',
  external: 'Scope: external',
  no_ssh_profile: 'Kein aktives SSH-Connection-Profil',
  no_matching_routine: 'Keine Routine passt zum Service-Typ',
  incompatible: 'Service-Typ passt zu keiner Routine',
  no_service_type: 'Kein Service-Typ hinterlegt',
};

export function exclusionLabel(reason: string): string {
  return EXCLUSION_LABELS[reason] ?? reason;
}

function ExcludedList({
  excluded,
}: {
  excluded: MaintenancePreviewExclusion[];
}): JSX.Element | null {
  if (excluded.length === 0) return null;
  return (
    <details className="mt-4 rounded-md border border-border" data-testid="batch-excluded">
      <summary className="cursor-pointer px-3 py-2 text-sm">
        <span className="font-medium">{excluded.length}</span>{' '}
        {excluded.length === 1 ? 'System bleibt außen vor' : 'Systeme bleiben außen vor'}{' '}
        <span className="text-muted">(Grund anzeigen)</span>
      </summary>
      <ul className="flex flex-col gap-1 border-t border-border p-3">
        {excluded.map((ex) => (
          <li
            key={ex.system_id}
            className="flex flex-wrap items-center gap-2 text-sm"
            data-testid={`batch-excluded-${ex.system_id}`}
          >
            <Link
              to="/systems/$id"
              params={{ id: ex.system_id }}
              className="font-mono text-xs text-primary hover:underline"
            >
              {ex.hostname}
            </Link>
            <span className="text-xs text-muted">{exclusionLabel(ex.reason)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
